/**
 * mrpRowIdentity.ts
 *
 * Authoritative row identity helper for MRP Planning UI.
 *
 * Critical invariants:
 * 1. Row identity is ready for: material_id + normalized UOM + structured material_spec.
 * 2. Rows are NOT keyed or grouped by material_code or material_name.
 * 3. material_id is authoritative.
 * 4. Technical AUTO-* codes are identified for de-emphasized display.
 * 5. Two rows with identical material_id and UOM but different structured material_spec
 *    stay strictly separate.
 */

import { serializeSpecKey } from '@/features/purchasing/specKey'
import type { StructuredMaterialSpec } from '../types/mrpTypes'

export interface BuildMrpRowKeyInput {
  row_id?: string
  material_id: string | null
  uom: string
  material_spec?: StructuredMaterialSpec | string | null
}

/**
 * Builds a deterministic canonical key for an MRP row.
 * Format: `<material_id>::<uom>::<serialized_spec>`
 */
export function buildMrpRowKey(input: BuildMrpRowKeyInput): string {
  if (input.row_id && input.row_id.trim() !== '') {
    return input.row_id.trim()
  }

  const mid = input.material_id ? input.material_id.trim() : '__unresolved_material__'
  const normalizedUom = input.uom ? input.uom.trim().toUpperCase() : '__no_uom__'
  const specKey = serializeSpecKey(
    typeof input.material_spec === 'string'
      ? input.material_spec
      : (input.material_spec as Record<string, unknown> | null),
  )

  return `${mid}::${normalizedUom}::${specKey || '__no_spec__'}`
}

/**
 * Checks if a material code is an auto-generated technical code (e.g. AUTO-VT-*, AUTO-*)
 * so the UI can de-emphasize it instead of showing it prominently.
 */
export function isAutoMaterialCode(code?: string | null): boolean {
  if (!code) return false
  return /^AUTO-/i.test(code.trim())
}
